/**
 * SearchModal — portal-rendered search overlay.
 *
 * Uses backend results when available (debounced) and falls back to a
 * client-side filter over loaded projects + chats otherwise.
 *
 * Phase E1: extracted from Sidebar.tsx.
 */

import { createPortal } from 'react-dom';
import { useEffect, useRef } from 'react';
import { Folder, MessageSquare, Search } from 'lucide-react';
import type { Project } from './types';
import type { SearchResult } from './useSidebarSearch';
import { SkeletonList } from '../../ui/skeleton';
import { ButtonSpinner } from '../../ui/ButtonSpinner';

type RecentChat = {
    id: string;
    name: string;
    projectId: string;
    projectName: string;
};

type Props = {
    open: boolean;
    searchQuery: string;
    onSearchChange: (q: string) => void;
    onClose: () => void;
    apiResults: { results: SearchResult[]; next: string | null } | null;
    isSearching: boolean;
    onLoadMore: () => void;

    projects: Project[];
    filteredProjects: Project[];
    allRecentChats: RecentChat[];

    onOpenProject: (projectId: string) => void;
    onSelectChat: (projectId: string, chatId: string) => void;
};

export default function SearchModal({
    open, searchQuery, onSearchChange, onClose,
    apiResults, isSearching, onLoadMore,
    projects, filteredProjects, allRecentChats,
    onOpenProject, onSelectChat,
}: Props) {
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (open) setTimeout(() => inputRef.current?.focus(), 100);
    }, [open]);

    // Global Esc listener — works even when focus isn't on the input
    // (e.g., user has clicked a result and is navigating with the keyboard).
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    const sq = searchQuery.toLowerCase().trim();

    return createPortal(
        <div className="search-modal-backdrop" onClick={onClose}>
            <div
                className="search-modal"
                role="dialog"
                aria-modal="true"
                aria-label="Search chats and projects"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="search-modal-header">
                    <Search size={18} className="search-modal-icon" />
                    <input
                        ref={inputRef}
                        type="text"
                        className="search-modal-input"
                        placeholder="Search chats and projects..."
                        value={searchQuery}
                        onChange={(e) => onSearchChange(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
                    />
                    <button type="button" className="search-modal-esc" onClick={onClose}>
                        ESC
                    </button>
                </div>
                {sq ? (
                    <SearchResultsView
                        apiResults={apiResults}
                        isSearching={isSearching}
                        onLoadMore={onLoadMore}
                        searchQuery={searchQuery}
                        filteredProjects={filteredProjects}
                        allRecentChats={allRecentChats}
                        onOpenProject={(id) => { onOpenProject(id); onClose(); }}
                        onSelectChat={(pid, cid) => { onSelectChat(pid, cid); onClose(); }}
                    />
                ) : (
                    <div className="search-results">
                        <div className="search-result-section">
                            <span className="search-result-label">Recent</span>
                            {allRecentChats.slice(0, 10).map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    className="search-result-item"
                                    onClick={() => { onSelectChat(c.projectId, c.id); onClose(); }}
                                >
                                    <MessageSquare size={16} />
                                    <span>{c.name}</span>
                                    {c.projectName && (
                                        <span className="chat-project-tag">{c.projectName}</span>
                                    )}
                                </button>
                            ))}
                        </div>
                        {projects.length > 0 && (
                            <div className="search-result-section">
                                <span className="search-result-label">Projects</span>
                                {projects.slice(0, 5).map((p) => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        className="search-result-item"
                                        onClick={() => { onOpenProject(p.id); onClose(); }}
                                    >
                                        <Folder size={16} />
                                        <span>{p.name}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>,
        document.body,
    );
}

type ResultsViewProps = {
    apiResults: { results: SearchResult[]; next: string | null } | null;
    isSearching: boolean;
    onLoadMore: () => void;
    searchQuery: string;
    filteredProjects: Project[];
    allRecentChats: RecentChat[];
    onOpenProject: (projectId: string) => void;
    onSelectChat: (projectId: string, chatId: string) => void;
};

function SearchResultsView({
    apiResults, isSearching, onLoadMore, searchQuery,
    filteredProjects, allRecentChats, onOpenProject, onSelectChat,
}: ResultsViewProps) {
    const apiItems = apiResults?.results ?? [];
    const hasApi = apiItems.length > 0;
    const clientItems: SearchResult[] = hasApi ? [] : [
        ...filteredProjects.map((p) => ({ type: 'project' as const, id: p.id, name: p.name })),
        ...allRecentChats.map((c) => ({
            type: 'chat' as const,
            id: c.id,
            name: c.name,
            project: c.projectId,
            project_name: c.projectName,
        })),
    ];
    const items = hasApi ? apiItems : clientItems;
    const projectItems = items.filter((r) => r.type === 'project');
    const chatItems = items.filter((r) => r.type === 'chat');
    const noResults = !isSearching && items.length === 0;

    return (
        <div className="search-results">
            {isSearching && <SkeletonList count={3} item="row" gap="sm" className="search-loading" />}
            {noResults && (
                <div className="search-no-results">No results for "{searchQuery}"</div>
            )}
            {projectItems.length > 0 && (
                <div className="search-result-section">
                    <span className="search-result-label">Projects</span>
                    {projectItems.map((r) => (
                        <button
                            key={r.id}
                            type="button"
                            className="search-result-item"
                            onClick={() => onOpenProject(r.id)}
                        >
                            <Folder size={16} />
                            <span>{r.name}</span>
                        </button>
                    ))}
                </div>
            )}
            {chatItems.length > 0 && (
                <div className="search-result-section">
                    <span className="search-result-label">
                        {hasApi ? 'Messages & Chats' : 'Chats'}
                    </span>
                    {chatItems.map((r) => (
                        <button
                            key={r.id}
                            type="button"
                            className="search-result-item"
                            onClick={() => onSelectChat(r.project ?? '', r.id)}
                        >
                            <MessageSquare size={16} />
                            <span>{r.name}</span>
                            {r.project_name && (
                                <span className="chat-project-tag">{r.project_name}</span>
                            )}
                        </button>
                    ))}
                </div>
            )}
            {apiResults?.next && (
                <button
                    type="button"
                    className="search-load-more"
                    onClick={onLoadMore}
                >
                    {isSearching ? <ButtonSpinner size="sm" /> : 'Load more'}
                </button>
            )}
        </div>
    );
}
